import { useDocEngine, useGetDocumentsTemplates, useGetTasks } from "@hooks";
import { Device, Document, RoadmapTask, User } from "@models";
import { Accordion, AccordionSummary } from "@mui/material";
import {
  captureSentryMessage,
  getDocTypeStatus,
  getDocumentContexts,
} from "@utils";
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { TaskAccordionCard } from "src/components/Roadmap/Task/TaskAccordionCard";
import { TEMPLATE_ID } from "./../../../stores/models";

type Props = {
  deviceId: string;
  task: RoadmapTask;
  documents: Document[];
  user: User;
  device: Device;
};

export const TaskAccordion = ({
  task,
  documents,
  user,
  deviceId,
  device,
}: Props) => {
  const { orgId = "" } = useParams();
  const docEngine = useDocEngine(deviceId);
  const [isLoading, setIsLoading] = useState(false);
  const accordionRef = useRef<HTMLDivElement>(null);
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: tasks } = useGetTasks(orgId, deviceId);

  const handleTaskClick = async (task: RoadmapTask) => {
    setIsLoading(true);
    // check if task.id is of type TEMPLATE_ID
    let templateKey: string | undefined;
    if (Object.values(TEMPLATE_ID).includes(task.taskKey as any))
      templateKey = task.taskKey;

    // if the the tasks is of type TEMPLATE_ID, we need to check more things. Otherwise we can just navigate to the link
    if (templateKey && templatesConfig) {
      await docEngine.navigateToWizardOrQMS({
        templateKey: templateKey,
        device,
        documents,
        user,
        templatesConfig,
        tasks,
      });
    } else {
      captureSentryMessage(
        "Task Modal was clicked but no where to go. Missing an old formId?"
      );
    }
    setIsLoading(false);
  };

  // Check if every document that is mentioned inside the context config in the TemplateConfig is present in the documents array and marked as completed
  // We need to check if we have a valid template type because task id can also be a different string
  let templateType: string | undefined;
  if (Object.values(TEMPLATE_ID).includes(task.taskKey as TEMPLATE_ID))
    templateType = task.taskKey;
  if (!templateType || !templatesConfig) return null;

  const { incompleteTemplates } = getDocumentContexts({
    templateKey: templateType,
    documents,
    user,
    orgId,
    device,
    templatesConfig,
    tasks,
  });

  let docStatus = getDocTypeStatus({
    documents,
    templateKey: templateType,
    user,
    ignoreEntitlement: true,
    orgId,
    device,
    templatesConfig,
    tasks,
  });

  return (
    <div key={task.title} className="flex flex-col gap-x-1 items-center">
      <Accordion
        sx={{
          width: "100%",
          "&.MuiAccordion-root": {
            boxShadow: "none",
            margin: "0",
          },
          "& .MuiButtonBase-root": {
            padding: "0",
            minHeight: "unset",
            "& .MuiAccordionSummary-content": {
              margin: "0",
            },
            "&.Mui-expanded": {
              minHeight: "unset",
            },
          },
        }}
        ref={accordionRef}
      >
        <AccordionSummary aria-controls="panel1-content" id="panel1-header">
          <TaskAccordionCard
            onClick={() => handleTaskClick(task)}
            task={task}
            docStatus={docStatus}
            isLoading={isLoading}
            incompleteTemplates={incompleteTemplates}
          />
        </AccordionSummary>
      </Accordion>
    </div>
  );
};
